/**
 * HostelBookingList.jsx — CAMP-RES-DEMO-01
 * All bookings for Kaveri Bhavan with status filters.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  CalendarCheck, ArrowLeft, Search, PlusCircle, ChevronRight,
  BedDouble, IndianRupee, User,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VCE_INST = "44444444-4444-4444-4444-444444444444";

const STATUS_CONFIG = {
  DRAFT:       { label: "Draft",       color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  RESERVED:    { label: "Reserved",    color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  CONFIRMED:   { label: "Confirmed",   color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  CHECKED_IN:  { label: "Checked In",  color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  CHECKED_OUT: { label: "Checked Out", color: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
  CANCELLED:   { label: "Cancelled",   color: "bg-red-500/20 text-red-300 border-red-500/30" },
};

export default function HostelBookingList() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");

  const institutionId = user?.institution_id || VCE_INST;

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/api/v1/hostel/bookings?institution_id=${institutionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setBookings(r.data?.bookings || r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, institutionId]);

  const filtered = useMemo(() => {
    let list = bookings;
    if (filterStatus !== "ALL") list = list.filter((b) => b.status === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (b) =>
          b.booking_reference?.toLowerCase().includes(q) ||
          b.student_name?.toLowerCase().includes(q) ||
          b.room_number?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [bookings, filterStatus, search]);

  const statuses = ["ALL", ...Object.keys(STATUS_CONFIG)];

  return (
    <div className="p-6 space-y-5" data-testid="hostel-booking-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/campus/hostel")}
            className="text-slate-400 hover:text-white" data-testid="btn-back-dashboard">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-amber-400" />
              <h1 className="text-xl font-bold text-white">Booking Register</h1>
            </div>
            <p className="text-slate-400 text-sm">Kaveri Bhavan · {bookings.length} bookings</p>
          </div>
        </div>
        <Button
          data-testid="btn-new-booking"
          onClick={() => navigate("/campus/hostel/bookings/new")}
          className="bg-amber-600 hover:bg-amber-500 text-white"
        >
          <PlusCircle className="w-4 h-4 mr-2" /> New Booking
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="booking-search-input"
            placeholder="Search reference, student, room…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-slate-800 border-slate-600 text-white placeholder-slate-500"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {statuses.map((s) => {
            const sc = STATUS_CONFIG[s];
            return (
              <button
                key={s}
                data-testid={`filter-booking-status-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  filterStatus === s
                    ? "bg-amber-600 text-white border-amber-500"
                    : "bg-slate-800 text-slate-400 border-slate-600 hover:border-amber-500"
                }`}
              >
                {s === "ALL" ? "All" : sc?.label || s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="text-sm text-slate-400">
        Showing <span className="text-white font-medium">{filtered.length}</span> of{" "}
        <span className="text-white font-medium">{bookings.length}</span> bookings
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <CalendarCheck className="w-8 h-8 mx-auto mb-2 animate-pulse" />
          Loading bookings…
        </div>
      ) : (
        <div className="rounded-xl border border-slate-700/50 overflow-hidden" data-testid="booking-table">
          <table className="w-full">
            <thead className="bg-slate-800/80 border-b border-slate-700/50">
              <tr>
                {["Reference", "Student", "Room", "Period", "Amount", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {filtered.map((b) => {
                const sc = STATUS_CONFIG[b.status] || { label: b.status, color: "bg-slate-500/20 text-slate-300 border-slate-500/30" };
                return (
                  <tr
                    key={b.id}
                    data-testid={`booking-row-${b.id}`}
                    className="bg-slate-800/40 hover:bg-slate-700/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/campus/hostel/bookings/${b.id}`)}
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-mono font-medium text-amber-300">{b.booking_reference}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-sm text-white">{b.student_name || b.student_id?.slice(0, 8) + "…"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <BedDouble className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-sm text-slate-200">
                          Room {b.room_number || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-slate-300">{b.check_in_date}</p>
                      <p className="text-xs text-slate-400">→ {b.check_out_date}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <IndianRupee className="w-3 h-3 text-amber-300" />
                        <span className="text-sm font-medium text-amber-300">
                          {(b.total_amount || 0).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`text-xs border ${sc.color}`}>{sc.label}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                    No bookings match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
